/**
 * This is the modal dialog that lets the user give feedback about things that
 * aren't working properly with the challenge.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";

import KUITextArea from "#shared-components-package/labeled-textarea.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon from "#shared-styles-package/deprecated-icon.tsx";

/* An enum of different feedback types. The keys are just useful reference
 * names, whereas the values -- which are a little vague on their own -- are
 * the values that the content issue reporting code expects. */
export enum FeedbackType {
    // This step is too hard.
    TOO_HARD = "inability",

    // I don't understand what the hint code means.
    DONT_UNDERSTAND_HINT = "hintcode",

    // I got an "oh noes" that I shouldn't have.
    UNEXPECTED_SYNTAX_ERROR = "syntax",

    // I did this step correctly, but it won't let me continue.
    GRADER_FALSE_NEGATIVE = "grader",

    // This step's instructions are confusing.
    INSTRUCTIONS_CONFUSING = "instructions",

    // The editor in my browser is broken.
    EDITOR_BROKEN = "editor",

    // Not translated to my language
    NOT_TRANSLATED = "translated",

    // Something else
    OTHER_FEEDBACK = "other",
}

export type Responses = Array<
    // @ts-expect-error - TS2344 - Type 'string | null | undefined' does not satisfy the constraint 'string | number | symbol'.
    Partial<Record<string | null | undefined, string>>
>;

type FeedbackDialogProps = {
    // Whether we expect that the challenge/scratchpad page is already
    // fully translated to the current language. Depending on this value
    // we will direct the user to do different things if they complain
    // about untranslated strings (FeedbackType.NOT_TRANSLATED).
    expectPageIsTranslated: boolean;
    // Resolve handler
    handleResolve: (
        responses: Responses | null | undefined,
        details: string | null | undefined,
        deadEnd: boolean,
    ) => Promise<unknown>;
    // We need the modal to be able to close itself.
    closeModal: () => void;
    // Callback to actually send feedback to our content issue tracker
    // -- this returns a promise
    sendFeedback: (
        feedbackType: FeedbackType,
        responses: Responses | null | undefined,
        details: string | null | undefined,
        deadEnd: boolean,
    ) => Promise<unknown>;
    // Whether there's currently a code warning on this step
    stepCodeWarning: boolean;
    // Whether there's currently a "go back a a step" notice
    stepGoBackNotice: boolean;
};

type FeedbackDialogState = {
    feedbackType: FeedbackType | null;
    error: boolean;
};

/* This is the actual modal dialog we will export */
class ChallengeFeedbackModal extends React.Component<
    Omit<FeedbackDialogProps, "handleResolve">,
    FeedbackDialogState
> {
    state: FeedbackDialogState = {
        feedbackType: null,
        error: false,
    };

    handleChooseFeedbackType: (type: FeedbackType) => void = (
        type: FeedbackType,
    ) => {
        this.setState({feedbackType: type});
    };

    handleResolve: (
        responses: Responses | null | undefined,
        details: string | null | undefined,
        deadEnd: boolean,
    ) => Promise<unknown> = (
        responses: Responses | null | undefined,
        details: string | null | undefined,
        deadEnd: boolean,
    ) => {
        this.setState({error: false});
        const sendFeedback = () =>
            this.props.sendFeedback(
                this.state.feedbackType || FeedbackType.OTHER_FEEDBACK,
                responses,
                details,
                deadEnd,
            );
        if (deadEnd) {
            // The user "dead-ended" by finding a potential solution.
            // It's okay to close the dialog without any visual feedback,
            // but we still want to send their response pathway.
            this.props.closeModal();
            return sendFeedback().catch((e) => {
                // The endpoint serves a 400 for dead-end responses
                if (e.response.status !== 400) {
                    throw e;
                }
            });
        }
        // The user has submitted to issue tracker using a DetailsForm.
        // We'll let them watch the request go through or (hopefully never)
        // fail so they can try again.
        return sendFeedback()
            .then(() => {
                this.props.closeModal();
            })
            .catch((e) => {
                this.setState({error: true});
                throw e;
            });
    };

    restart: () => void = () => {
        this.setState({
            feedbackType: null,
            error: false,
        });
    };

    renderErrorMessage():
        | undefined
        | React.ReactElement<React.ComponentProps<"em">> {
        if (this.state.error) {
            return (
                <em>
                    {t`There was a problem submitting your feedback. Try again?`}
                </em>
            );
        }
    }

    renderFeedbackTypeMenu: () => React.ReactNode = () => {
        const FeedbackTypeStrings = getFeedbackTypeStrings();
        const radios = Object.keys(FeedbackType).map((k, i) => {
            const feedbackType = FeedbackType[k];
            return (
                <Radio
                    key={i}
                    onClick={() => this.handleChooseFeedbackType(feedbackType)}
                >
                    {FeedbackTypeStrings[feedbackType]}
                </Radio>
            );
        });
        return <form>{radios}</form>;
    };

    renderTitle: () => string = () => {
        return this.state.feedbackType
            ? getFeedbackTypeStrings()[this.state.feedbackType]
            : t`Which best describes your problem?`;
    };

    renderFooter: () => React.ReactNode = () => {
        return (
            this.state.feedbackType && (
                <Button onClick={() => this.restart()} kind="tertiary">
                    {"< " + t`Report a different problem`}
                </Button>
            )
        );
    };

    renderOptionsForFeedbackType(): React.ReactNode {
        if (!this.state.feedbackType) {
            return null;
        }
        const Dialog = FeedbackDialogs[this.state.feedbackType];
        return <Dialog handleResolve={this.handleResolve} {...this.props} />;
    }

    render(): React.ReactNode {
        return (
            <OnePaneDialog
                title={this.renderTitle()}
                content={
                    <View>
                        {this.state.feedbackType === null
                            ? this.renderFeedbackTypeMenu()
                            : this.renderOptionsForFeedbackType()}
                        {this.renderErrorMessage()}
                    </View>
                }
                footer={this.renderFooter()}
            />
        );
    }
}

/* Some high-level descriptions of each feedback type */
const getFeedbackTypeStrings = () => ({
    [FeedbackType.TOO_HARD]: t`This step is too hard, I don't feel prepared enough to do it.`,
    [FeedbackType.DONT_UNDERSTAND_HINT]: t`I don't understand what the hint code with the boxes and blanks mean.`,
    [FeedbackType.UNEXPECTED_SYNTAX_ERROR]: t`There's an "Oh Noes!" message, and I can't get it to go away.`,
    [FeedbackType.GRADER_FALSE_NEGATIVE]: t`I did this step correctly, but it won't let me continue.`,
    [FeedbackType.INSTRUCTIONS_CONFUSING]: t`This step's instructions are confusing. I'm not sure what it wants me to do.`,
    [FeedbackType.EDITOR_BROKEN]: t`The editor in my browser is broken.`,
    [FeedbackType.NOT_TRANSLATED]: t`I don't speak English — and the instructions aren't translated.`,
    [FeedbackType.OTHER_FEEDBACK]: t`I have other feedback.`,
});

type RadioProps = {
    checked?: boolean;
    children: React.ReactNode;
    name?: string;
    onClick: (e: React.ChangeEvent<HTMLInputElement>) => unknown;
    value?: string;
};

/* A radiobutton-label combination, rolled into one */
const Radio = (props: RadioProps) => (
    <div>
        <label className={css(styles.radio)}>
            <input
                type="radio"
                name={props.name}
                value={props.value}
                checked={!!props.checked}
                onChange={props.onClick}
            />{" "}
            {props.children}
        </label>
    </div>
);

type QuestionProps = {
    children: React.ReactNode;
};

/* Literally just a question label */
const Question = (props: QuestionProps) => (
    <h4 className={css(styles.question)}>{props.children}</h4>
);

type DetailsFormProps = {
    // Callback when the submit button is clicked
    onSubmit: (details: string) => Promise<unknown>;
};

type DetailsFormState = {
    details: string;
    submitting: boolean;
};

/* A textarea and a way to submit the text */
class DetailsForm extends React.Component<DetailsFormProps, DetailsFormState> {
    state: DetailsFormState = {
        details: "",
        submitting: false,
    };

    handleUpdate = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({details: e.target.value});
    };

    handleSubmit = () => {
        this.setState({submitting: true});
        this.props
            .onSubmit(this.state.details)
            .catch(() => this.setState({submitting: false}));
    };

    render(): React.ReactNode {
        const buttonLabel = this.state.submitting
            ? t`Submitting Feedback...`
            : t`Give Feedback`;
        return (
            <div>
                <div>
                    <KUITextArea
                        className={css(styles.feedbackTextArea)}
                        onChange={this.handleUpdate}
                        title=""
                        height="150"
                    />
                </div>
                <Button onClick={this.handleSubmit} kind="secondary">
                    {buttonLabel}
                </Button>
            </div>
        );
    }
}

type StepProps = {
    children: React.ReactNode;
    // The name of the step -- the key in the key-value pair that will be sent
    // to the issue tracker, the value being the value of the selected Radio.
    name?: string;
};

/* A step in a multi-step Q&A grilling with the dialog */
const Step = (props: StepProps) => <div>{props.children}</div>;

type DialogProps = {
    // Please someone think of the children
    children: React.ReactNode;
    // A callback which will fire with the current child when only one
    // is displayed.
    onSingleChildShown?: (
        child: React.ReactElement<React.ComponentProps<typeof Step>>,
    ) => unknown;
    // A Dialog can just show one of its children in order to faciliate
    // multi-step interactions. If false, it will show all types.
    showSingleChild: number | false;
};

type DefaultDialogProps = {
    showSingleChild: DialogProps["showSingleChild"];
};

type DialogState = Record<any, any>;

/* A base class for a dialog, which here is something
 * that lives _inside_ the modal */
class Dialog extends React.Component<DialogProps, DialogState> {
    static defaultProps: DefaultDialogProps = {
        showSingleChild: false,
    };

    componentDidMount() {
        this._maybeCallSingleChildCallback();
    }

    componentDidUpdate(prevProps: DialogProps, prevState: DialogState) {
        if (prevProps.showSingleChild !== this.props.showSingleChild) {
            this._maybeCallSingleChildCallback();
        }
    }

    _maybeCallSingleChildCallback = () => {
        if (
            this.props.showSingleChild !== false &&
            this.props.onSingleChildShown
        ) {
            const {onSingleChildShown} = this.props;
            onSingleChildShown(this.shownChild());
        }
    };

    shownChild = (): React.ReactElement<React.ComponentProps<typeof Step>> => {
        const children = React.Children.toArray(this.props.children);
        // @ts-expect-error - TS2322 - Type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal' is not assignable to type 'ReactElement<StepProps, string | JSXElementConstructor<any>>'.
        return children[
            Math.min(
                // Although JavaScript is fine calling Math.max with false, TypeScript
                // is not so we pass 0 when showSingleChild is false.
                Math.max(this.props.showSingleChild || 0, 0),
                children.length - 1,
            )
        ];
    };

    render(): React.ReactNode {
        return (
            <div>
                {this.props.showSingleChild !== false
                    ? this.shownChild()
                    : this.props.children}
            </div>
        );
    }
}

type SimpleFeedbackDialogProps = {
    children: React.ReactNode;
    // Resolve handler
    handleResolve: (
        responses: Responses | null | undefined,
        details: string,
        deadEnd: boolean,
    ) => Promise<unknown>;
    // Whether to hide the feedback textarea
    hideFeedback: boolean;
};

type DefaultSimpleFeedbackDialogProps = {
    hideFeedback: SimpleFeedbackDialogProps["hideFeedback"];
};

/* A simple dialog that throws down some text
 * and asks the user to give feedback. */
class SimpleFeedbackDialog extends React.Component<SimpleFeedbackDialogProps> {
    static defaultProps: DefaultSimpleFeedbackDialogProps = {
        hideFeedback: false,
    };

    handleSubmitDetails = (details: string): Promise<unknown> => {
        return this.props.handleResolve(null, details, false);
    };

    render(): React.ReactNode {
        return (
            <Dialog>
                {this.props.children}
                {!this.props.hideFeedback && (
                    <DetailsForm onSubmit={this.handleSubmitDetails} />
                )}
            </Dialog>
        );
    }
}

const icons = {
    warningSign: {
        path: "M57.379 77.077l0-10.633q0-.784-.539-1.323t-1.225-.539l-10.78 0q-.784 0-1.274.539t-.49 1.323l0 10.633q0 .784.539 1.323t1.225.539l10.78 0q.784 0 1.274-.539t.49-1.323zm-2.009-19.649q1.813 0 1.911-1.274l1.029-25.725q.049-.686-.637-1.176t-1.274-.49l-12.348 0q-.686 0-1.274.49t-.637 1.274l.98 25.627q0 1.176 1.911 1.274l10.339 0zm37.877 35.819l-86.044 0q-4.165 0-6.174-3.528t-.098-7.056l43.022-78.841q.98-1.715 2.646-2.744 3.577-2.156 7.252 0 1.715 1.029 2.646 2.744l43.022 78.841q1.96 3.528-.147 7.056-.931 1.568-2.597 2.548t-3.528.98z",
        width: 100,
        height: 92.816,
    },
} as const;

const styles = StyleSheet.create({
    feedbackTextArea: {
        boxSizing: "border-box",
        minHeight: 50,
        width: "100%",
    },
    orangeWarning: {
        color: "darkorange",
        fontWeight: "bold",
    },
    radio: {
        display: "block",
        padding: 4,
    },
    question: {
        marginBottom: 10,
    },
});

type StepState = {
    step: number;
    currentStepName: string | null | undefined;
    // @ts-expect-error - TS2344 - Type 'string | null | undefined' does not satisfy the constraint 'string | number | symbol'.
    responses: Array<Partial<Record<string | null | undefined, string>>>;
};

class TooHardDialog extends React.Component<FeedbackDialogProps, StepState> {
    state: StepState = {
        step: 0,
        currentStepName: null,
        responses: [],
    };

    handleStepShown = (
        child: React.ReactElement<React.ComponentProps<typeof Step>>,
    ) => {
        this.setState({
            currentStepName: child.props.name,
        });
    };

    handleSubmitDetails = (details: string): Promise<unknown> => {
        return this.props.handleResolve(this.state.responses, details, false);
    };

    handleDeadEnd = (): Promise<unknown> => {
        return this.props.handleResolve(this.state.responses, null, true);
    };

    nextStep(value: string): () => void {
        return () => {
            const response = {
                [this.state.currentStepName || "null"]: value,
            } as const;
            this.setState({
                responses: this.state.responses.concat(response),
                step: this.state.step + 1,
            });
        };
    }

    render(): React.ReactNode {
        return (
            <Dialog
                showSingleChild={this.state.step}
                onSingleChildShown={this.handleStepShown}
            >
                <Step name="grader-inability-watched">
                    <Question>
                        {t`Did you watch the talk-through that came before this challenge?`}
                    </Question>
                    <Radio
                        onClick={this.handleDeadEnd}
                        value="no-trynow"
                        children={t`No, I'll try that now.`}
                    />
                    <Radio onClick={this.nextStep("yes")}>
                        {t`Yes, and it didn't prepare me enough.`}
                    </Radio>
                </Step>
                <Step name="grader-inability-rewatched">
                    <Question>
                        {t`Sometimes it helps to re-watch the talk-through. Have you tried that?`}
                    </Question>
                    <Radio
                        onClick={this.handleDeadEnd}
                        value="no-trynow"
                        children={t`No, I'll try that now.`}
                    />
                    <Radio onClick={this.nextStep("no-wonthelp")}>
                        {t`No, I don't think it will help.`}
                    </Radio>
                    <Radio onClick={this.nextStep("yes")}>{t`Yes!`}</Radio>
                </Step>
                <Step name="grader-inability-tinkered">
                    <Question>
                        {t`It can help to play with the final code at the end of the talk-through, going through it line by line and editing it to see how the ouput changes. Have you tried that?`}
                    </Question>
                    <Radio
                        onClick={this.handleDeadEnd}
                        value="no-trynow"
                        children={t`No, I'll try that now.`}
                    />
                    <Radio onClick={this.nextStep("yes")}>{t`Yes!`}</Radio>
                </Step>
                <Step name="grader-inability-question">
                    <Question>
                        {t`Sometimes it helps to ask a question underneath the talk-through, when something is confusing. Did you try that?`}
                    </Question>
                    <Radio
                        onClick={this.handleDeadEnd}
                        value="no-trynow"
                        children={t`No, I'll try that now.`}
                    />
                    <Radio onClick={this.nextStep("yes-no-questions")}>
                        {t`No, but I had no questions`}
                    </Radio>
                    <Radio onClick={this.nextStep("yes-answer-didnthelp")}>
                        {t`Yes, but the answer didn't help.`}
                    </Radio>
                    <Radio onClick={this.nextStep("yes-no-answer")}>
                        {t`Yes, but nobody answered.`}
                    </Radio>
                </Step>
                <Step>
                    <Question>
                        {t`Thank you for answering those questions. Please give us more details on what you feel under-prepared for:`}
                    </Question>
                    <DetailsForm onSubmit={this.handleSubmitDetails} />
                </Step>
            </Dialog>
        );
    }
}

class DontUnderstandHintDialog extends React.Component<
    FeedbackDialogProps,
    StepState
> {
    state: StepState = {
        step: 0,
        currentStepName: null,
        responses: [],
    };

    handleStepShown = (
        child: React.ReactElement<React.ComponentProps<typeof Step>>,
    ) => {
        this.setState({
            currentStepName: child.props.name,
        });
    };

    handleSubmitDetails = (details: string): Promise<unknown> => {
        return this.props.handleResolve(this.state.responses, details, false);
    };

    handleDeadEnd = (): Promise<unknown> => {
        return this.props.handleResolve(this.state.responses, null, true);
    };

    nextStep(value: string): () => void {
        return () => {
            const response = {
                [this.state.currentStepName || "null"]: value,
            } as const;
            this.setState({
                responses: this.state.responses.concat(response),
                step: this.state.step + 1,
            });
        };
    }

    render(): React.ReactNode {
        return (
            <Dialog
                showSingleChild={this.state.step}
                onSingleChildShown={this.handleStepShown}
            >
                <Step name="hintcode">
                    <Question>
                        {t`There's a "What's this?" link next to the hint code. If you click that, you'll get an explanation of what it means. Have you read that?`}
                    </Question>
                    <Radio
                        onClick={this.handleDeadEnd}
                        value="no-trynow"
                        children={t`No, I'll try that now.`}
                    />
                    <Radio onClick={this.nextStep("read-it-still-confusing")}>
                        {t`Yes, I have read the explanation, and it does not make sense.`}
                    </Radio>
                </Step>
                <Step>
                    <Question>
                        {t`What is confusing about the hint code?`}
                    </Question>
                    <DetailsForm onSubmit={this.handleSubmitDetails} />
                </Step>
            </Dialog>
        );
    }
}

class OhNoesDialog extends React.Component<FeedbackDialogProps, StepState> {
    state: StepState = {
        step: 0,
        currentStepName: null,
        responses: [],
    };

    handleStepShown = (
        child: React.ReactElement<React.ComponentProps<typeof Step>>,
    ) => {
        this.setState({
            currentStepName: child.props.name,
        });
    };

    handleSubmitDetails = (details: string): Promise<unknown> => {
        return this.props.handleResolve(this.state.responses, details, false);
    };

    handleDeadEnd = (): Promise<unknown> => {
        return this.props.handleResolve(this.state.responses, null, true);
    };

    nextStep(value: string): () => void {
        return () => {
            const response = {
                [this.state.currentStepName || "null"]: value,
            } as const;
            this.setState({
                responses: this.state.responses.concat(response),
                step: this.state.step + 1,
            });
        };
    }

    render(): React.ReactNode {
        return (
            <Dialog
                showSingleChild={this.state.step}
                onSingleChildShown={this.handleStepShown}
            >
                <Step name="syntax-compare-to-hintcode">
                    <Question>
                        {t`Have you tried comparing your code to the hint code on the right?`}
                    </Question>
                    <Radio
                        onClick={this.handleDeadEnd}
                        value="no-trynow"
                        children={t`No, I'll try that now.`}
                    />
                    <Radio onClick={this.nextStep("yes-but-not-in-hint")}>
                        {t`Yes, but it doesn't help because my code isn't in the hint code.`}
                    </Radio>
                    <Radio onClick={this.nextStep("yes-but-cant-see-diff")}>
                        {t`Yes, but I don't see the difference.`}
                    </Radio>
                </Step>
                <Step name="syntax-message-make-sense">
                    <Question>{t`Does the message make sense?`}</Question>
                    <Radio onClick={this.nextStep("no")}>
                        {t`No, it doesn't make sense.`}
                    </Radio>
                    <Radio onClick={this.nextStep("kindof")}>
                        {t`It makes a little sense.`}
                    </Radio>
                    <Radio onClick={this.nextStep("yes")}>
                        {t`Yes, it makes sense.`}
                    </Radio>
                </Step>
                <Step name="syntax-tried-fixing">
                    <Question>
                        {t`Have you tried anything to fix the syntax error?`}
                    </Question>
                    <Radio
                        onClick={this.handleDeadEnd}
                        value="no-trynow"
                        children={t`No, I'll try that now.`}
                    />
                    <Radio onClick={this.nextStep("no-dont-know-how")}>
                        {t`No, I don't know what I can do.`}
                    </Radio>
                    <Radio onClick={this.nextStep("yes")}>
                        {t`Yes, but it didn't work.`}
                    </Radio>
                </Step>
                <Step>
                    <Question>
                        {t`If you tried to fix it, what did you do? If you didn't, you can leave this blank.`}
                    </Question>
                    <DetailsForm onSubmit={this.handleSubmitDetails} />
                </Step>
            </Dialog>
        );
    }
}

const REASONS = {
    TRIED_TO_FIX: "tried-to-fix-it" as const,
    DONT_UNDERSTAND: "dont-understand" as const,
} as const;

type StepFalseNegativeDialogState = {
    reason: (typeof REASONS)[keyof typeof REASONS] | null | undefined;
};

class StepFalseNegativeDialog extends React.Component<
    FeedbackDialogProps,
    StepFalseNegativeDialogState
> {
    state: StepFalseNegativeDialogState = {
        reason: null,
    };

    handleSubmitDetails = (details: string): Promise<unknown> => {
        return this.props.handleResolve(
            this.state.reason && [{"grader-message": this.state.reason}],
            details,
            false,
        );
    };

    renderWarning(): React.ReactNode {
        return (
            <div>
                <p className={css(styles.orangeWarning)}>
                    <DeprecatedIcon icon={icons.warningSign} />{" "}
                    {t`It looks like there's an orange warning message for this step. Please read the message.`}
                </p>
                <span>
                    {t`If you still want to report a problem, please help us understand more:`}
                </span>
                <Radio
                    name="grader-message"
                    checked={this.state.reason === REASONS.TRIED_TO_FIX}
                    onClick={() =>
                        this.setState({
                            reason: REASONS.TRIED_TO_FIX,
                        })
                    }
                >
                    {t`I tried to fix it according to the message, but it still won't accept it.`}
                </Radio>
                <Radio
                    name="grader-message"
                    checked={this.state.reason === REASONS.DONT_UNDERSTAND}
                    onClick={() =>
                        this.setState({
                            reason: REASONS.DONT_UNDERSTAND,
                        })
                    }
                >
                    {t`I don't understand what the message wants me to do.`}
                </Radio>
            </div>
        );
    }

    renderGoBack(): React.ReactNode {
        return (
            <p className={css(styles.orangeWarning)}>
                <DeprecatedIcon icon={icons.warningSign} />{" "}
                {t`It looks like there's a message instructing you to go back a step. Check that step to see if something's wrong there that makes sense.`}
                {t`If the message in that step does not make sense, please report a problem from there.`}
            </p>
        );
    }

    render(): React.ReactNode {
        const goBack = this.props.stepGoBackNotice;
        const warning = this.props.stepCodeWarning;
        return (
            <Dialog>
                <p>
                    {t`Our automated grader checks your code before passing you to the next step, but sometimes it doesn't consider all the possible ways to code something.`}
                </p>
                {goBack && this.renderGoBack()}
                {warning && !goBack && this.renderWarning()}
                {!goBack && (
                    <span>
                        <p>
                            {t`We will automatically record your current code in the problem report, and try to see why the grader did not pass it. You can give additional details here:`}
                        </p>
                        <DetailsForm onSubmit={this.handleSubmitDetails} />
                    </span>
                )}
            </Dialog>
        );
    }
}

const StepConfusingDialog = (props: FeedbackDialogProps) => (
    <SimpleFeedbackDialog handleResolve={props.handleResolve}>
        {t`Sorry if our instructions are confusing! Please describe what is unclear about the instructions:`}
    </SimpleFeedbackDialog>
);

const zendeskUrl =
    "https://khanacademy.zendesk.com/hc/en-us/requests/new?ticket_form_id=37070";

const EditorBrokenDialog = (props: FeedbackDialogProps) => (
    <SimpleFeedbackDialog
        handleResolve={props.handleResolve}
        hideFeedback={true}
    >
        <p>
            {t`If you are on an iPad, we have various known issues on that browser. However, the editor should work in Chrome, Firefox, and Internet Explorer 9 or higher.`}{" "}
            {t`If it is not working on one of those browsers, please follow the following link to report an issue. Thanks!`}
        </p>
        <Button kind="tertiary" href={zendeskUrl} target="_blank">
            {t`Report an issue`}
        </Button>
    </SimpleFeedbackDialog>
);

const translationDashboardUrl = "http://khanacademy.org/translations";
const translationRequestUrl = "mailto:translations@khanacademy.org";

const NoTranslationDialog = (props: FeedbackDialogProps) => {
    if (props.expectPageIsTranslated) {
        return (
            <SimpleFeedbackDialog handleResolve={props.handleResolve}>
                <Question>{t`Which part is not translated?`}</Question>
            </SimpleFeedbackDialog>
        );
    }
    return (
        <Dialog>
            <p>
                {t`You can see the current status of translations on Khan Academy from the translation dashboard:`}
            </p>
            <Button
                href={translationDashboardUrl}
                target="_blank"
                kind="tertiary"
            >
                {t`Translation dashboard`}
            </Button>
            <p>
                {t`If your language is not on that list, you can send us an email to request that it be added:`}
            </p>
            <Button
                href={translationRequestUrl}
                target="_blank"
                kind="tertiary"
            >
                {t`Request a translation!`}
            </Button>
        </Dialog>
    );
};

const OtherFeedbackDialog = (props: FeedbackDialogProps) => (
    <SimpleFeedbackDialog handleResolve={props.handleResolve}>
        <p>{t`We like feedback!`}</p>
        <p>
            {t`Before you leave feedback here, please make sure your report doesn't fall into any of the other categories. You'll get better help that way!`}
        </p>
        <Question>{t`What other feedback do you have for us?`}</Question>
    </SimpleFeedbackDialog>
);

const FeedbackDialogs = {
    [FeedbackType.TOO_HARD]: TooHardDialog,
    [FeedbackType.DONT_UNDERSTAND_HINT]: DontUnderstandHintDialog,
    [FeedbackType.UNEXPECTED_SYNTAX_ERROR]: OhNoesDialog,
    [FeedbackType.GRADER_FALSE_NEGATIVE]: StepFalseNegativeDialog,
    [FeedbackType.INSTRUCTIONS_CONFUSING]: StepConfusingDialog,
    [FeedbackType.EDITOR_BROKEN]: EditorBrokenDialog,
    [FeedbackType.NOT_TRANSLATED]: NoTranslationDialog,
    [FeedbackType.OTHER_FEEDBACK]: OtherFeedbackDialog,
} as const;

export default ChallengeFeedbackModal;
